import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { AlertTriangle, Loader2, Gift, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { cobrosExpensasApi } from '../../../lib/api';

const fmtMoney = (n) => {
  const v = Math.round(Number(n || 0));
  return `$ ${v.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
};

const METODOS = [
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'mercado_pago', label: 'Mercado Pago' },
  { value: 'otro', label: 'Otro' },
];

const VTO_LABEL = { 1: '1° Vencimiento', 2: '2° Vencimiento', 3: '3° Vencimiento' };
const VTO_COLOR = { 1: 'bg-emerald-100 text-emerald-700', 2: 'bg-amber-100 text-amber-700', 3: 'bg-rose-100 text-rose-700' };

const hoyISO = () => new Date().toISOString().slice(0, 10);

export function RegistrarPagoDialog({ open, onClose, edificioId, periodoId, cobro, onSaved }) {
  const [monto, setMonto] = useState('');
  const [fechaPago, setFechaPago] = useState(hoyISO());
  const [metodoPago, setMetodoPago] = useState('transferencia');
  const [referencia, setReferencia] = useState('');
  const [observacion, setObservacion] = useState('');
  const [bonificar, setBonificar] = useState(false);
  const [vtoBonificado, setVtoBonificado] = useState(1);
  const [motivoBonif, setMotivoBonif] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmExceso, setConfirmExceso] = useState(false);

  // Tiers / fechas del período (vienen en el cobro)
  const tierPorFecha = (fecha) => {
    const f1 = cobro?.fecha_vto1, f2 = cobro?.fecha_vto2;
    if (f1 && fecha <= f1) return 1;
    if (f2 && fecha <= f2) return 2;
    return 3;
  };
  const montoTier = (t) => {
    const map = { 1: cobro?.monto_vto1, 2: cobro?.monto_vto2, 3: cobro?.monto_vto3 };
    return Number(map[t] ?? cobro?.monto_liquidado ?? 0);
  };
  const extra = Number(cobro?.deuda_anterior || 0) + Number(cobro?.intereses || 0);
  const cobradoPrev = Number(cobro?.total_cobrado || 0);
  const exigibleTier = (t) => Math.max(0, montoTier(t) + extra - cobradoPrev);

  const naturalTier = useMemo(() => tierPorFecha(fechaPago), [fechaPago, cobro]); // eslint-disable-line
  const vtoAplicado = bonificar ? Number(vtoBonificado) : naturalTier;
  const montoSugerido = exigibleTier(vtoAplicado);
  const recargoPerdonado = bonificar ? Math.max(0, montoTier(naturalTier) - montoTier(vtoAplicado)) : 0;

  // Al abrir o al cambiar fecha/bonificación → recalcular monto sugerido
  React.useEffect(() => {
    if (open) {
      setFechaPago(hoyISO());
      setMetodoPago('transferencia');
      setReferencia('');
      setObservacion('');
      setBonificar(false);
      setVtoBonificado(1);
      setMotivoBonif('');
      setConfirmExceso(false);
    }
  }, [open, cobro]);

  React.useEffect(() => {
    if (!open) return;
    const sug = exigibleTier(bonificar ? Number(vtoBonificado) : tierPorFecha(fechaPago));
    setMonto(sug > 0 ? String(sug) : '');
    setConfirmExceso(false);
  }, [open, fechaPago, bonificar, vtoBonificado, cobro]); // eslint-disable-line

  if (!cobro) return null;

  const montoNum = parseInt(String(monto).replace(/\D/g, ''), 10) || 0;
  const saldoActual = Number(cobro.saldo_pendiente || 0);
  const excede = montoNum > montoSugerido && montoSugerido > 0;
  const faltaMotivo = bonificar && !motivoBonif.trim();
  const invalid = montoNum <= 0 || !fechaPago || faltaMotivo;

  const handleSave = async () => {
    if (invalid) {
      if (faltaMotivo) toast.error('Indicá el motivo de la bonificación de recargo');
      else toast.error('Ingresá un monto válido y la fecha de pago');
      return;
    }
    if (excede && !confirmExceso) {
      setConfirmExceso(true);
      return;
    }
    setSaving(true);
    try {
      await cobrosExpensasApi.registrarPago(edificioId, periodoId, cobro.unidad_id, {
        monto: montoNum,
        fecha_pago: fechaPago,
        metodo_pago: metodoPago,
        referencia: referencia || null,
        observacion: observacion || null,
        bonificar_recargo: bonificar,
        vto_aplicado: bonificar ? Number(vtoBonificado) : null,
        motivo_bonificacion: bonificar ? motivoBonif.trim() : null,
      });
      toast.success(bonificar ? 'Pago registrado con bonificación de recargo' : 'Pago registrado');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al registrar el pago');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="dialog-registrar-pago">
        <DialogHeader>
          <DialogTitle>Registrar pago — {cobro.unidad_nombre}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Monto vigente hoy</span><span className="font-medium">{fmtMoney(cobro.total_a_cobrar)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Total cobrado</span><span className="font-medium text-emerald-700">{fmtMoney(cobro.total_cobrado)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Saldo pendiente</span><span className="font-semibold text-rose-700">{fmtMoney(cobro.saldo_pendiente)}</span></div>
          </div>

          {/* Fecha real de pago */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="pago-fecha" className="text-xs flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Fecha real de pago</Label>
              <Input id="pago-fecha" data-testid="input-pago-fecha" type="date" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Método</Label>
              <Select value={metodoPago} onValueChange={setMetodoPago}>
                <SelectTrigger data-testid="select-pago-metodo" className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {METODOS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Vencimiento según fecha + monto sugerido */}
          <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2" data-testid="sugerido-box">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-600">Vencimiento según la fecha</span>
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${VTO_COLOR[naturalTier]}`} data-testid="vto-natural-badge">
                {VTO_LABEL[naturalTier]}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-600">Monto sugerido {bonificar ? `(${VTO_LABEL[vtoAplicado]})` : ''}</span>
              <span className="text-base font-bold font-mono text-indigo-900" data-testid="monto-sugerido">{fmtMoney(montoSugerido)}</span>
            </div>
          </div>

          {/* Perdonar recargo */}
          {naturalTier > 1 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2">
              <label className="flex items-center gap-2 text-xs font-medium text-amber-800 cursor-pointer">
                <Checkbox checked={bonificar} onCheckedChange={(v) => setBonificar(!!v)} data-testid="check-bonificar" />
                <Gift className="w-3.5 h-3.5" /> Perdonar recargo / bonificar demora
              </label>
              {bonificar && (
                <div className="space-y-2 pl-1">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Cobrar como</Label>
                    <Select value={String(vtoBonificado)} onValueChange={(v) => setVtoBonificado(Number(v))}>
                      <SelectTrigger data-testid="select-vto-bonificado" className="text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: naturalTier - 1 }, (_, i) => i + 1).map((t) => (
                          <SelectItem key={t} value={String(t)}>{VTO_LABEL[t]} — {fmtMoney(montoTier(t))}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {recargoPerdonado > 0 && (
                    <div className="text-[11px] text-amber-700">Recargo perdonado: <strong>{fmtMoney(recargoPerdonado)}</strong> (no quedará como deuda).</div>
                  )}
                  <div className="space-y-1.5">
                    <Label htmlFor="pago-motivo-bonif" className="text-xs">Motivo de la bonificación <span className="text-rose-600">*</span></Label>
                    <Textarea
                      id="pago-motivo-bonif"
                      data-testid="input-motivo-bonificacion"
                      rows={2}
                      value={motivoBonif}
                      onChange={(e) => setMotivoBonif(e.target.value)}
                      placeholder="Ej: pago acreditado en término, imputado tarde por demora bancaria"
                      className={faltaMotivo ? 'border-rose-300' : ''}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Monto efectivamente cobrado */}
          <div className="space-y-1.5">
            <Label htmlFor="pago-monto" className="text-xs">Monto efectivamente cobrado</Label>
            <Input
              id="pago-monto"
              data-testid="input-pago-monto"
              type="text"
              inputMode="numeric"
              value={monto}
              onChange={(e) => setMonto(e.target.value.replace(/\D/g, ''))}
              placeholder="Ej: 50000"
              className="text-right text-base font-medium"
            />
            {excede && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex gap-1.5 items-start">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  El monto supera el sugerido en <strong>{fmtMoney(montoNum - montoSugerido)}</strong>.
                  {confirmExceso ? ' Confirmá guardar para continuar.' : ' Apretá Guardar de nuevo para confirmar.'}
                </span>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pago-ref" className="text-xs">Referencia / N° de operación (opcional)</Label>
            <Input id="pago-ref" data-testid="input-pago-referencia" value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="Ej: OP-123456" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pago-obs" className="text-xs">Observación (opcional)</Label>
            <Textarea id="pago-obs" data-testid="input-pago-observacion" rows={2} value={observacion} onChange={(e) => setObservacion(e.target.value)} />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="btn-pago-cancelar">Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || invalid} data-testid="btn-pago-guardar">
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
            {excede && !confirmExceso ? 'Revisar y guardar' : 'Guardar pago'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RegistrarPagoDialog;
